// ABOUTME: Suggested-links browser for the Customize sheet.
// ABOUTME: Search across all categories with favicons and add confirmation.

import React, { useMemo, useState } from "react";
import { POPULAR_LINKS } from "../../data/popularLinks";
import { normalizeUrlKey } from "../../utils/urlValidation";

interface SuggestedLink {
  url: string;
  name: string;
}

interface SuggestedLinksTabProps {
  onAddLink: (link: { url: string; name?: string; tags?: string[] }) => void;
  /** URLs already on the dashboard. Those rows show "Added" instead of a plus. */
  existingUrls?: string[];
}

/** Category is applied as a tag so the link is findable in the header search later. */
function categoryFor(url: string): string | undefined {
  return POPULAR_LINKS.find((cat) => cat.links.some((l) => l.url === url))?.category;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const SuggestedLinksTab: React.FC<SuggestedLinksTabProps> = ({ onAddLink, existingUrls }) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  // Just-added flash, kept briefly so the row confirms the click even before
  // the dashboard subscription reports the new link back.
  const [added, setAdded] = useState<Record<string, boolean>>({});
  const [failedIcons, setFailedIcons] = useState<Record<string, boolean>>({});

  const query = search.trim().toLowerCase();

  const onDashboard = useMemo(
    () => new Set((existingUrls ?? []).map((u) => normalizeUrlKey(u))),
    [existingUrls]
  );
  const isAdded = (url: string) => !!added[url] || onDashboard.has(normalizeUrlKey(url));

  const sections = useMemo(() => {
    return POPULAR_LINKS.filter((cat) => !category || cat.category === category)
      .map((cat) => ({
        category: cat.category,
        links: cat.links.filter((link) =>
          query
            ? link.name.toLowerCase().includes(query) || link.url.toLowerCase().includes(query)
            : true
        ),
      }))
      .filter((cat) => cat.links.length > 0);
  }, [category, query]);

  const handleAdd = (link: SuggestedLink) => {
    if (isAdded(link.url)) return;
    const cat = categoryFor(link.url);
    onAddLink({ ...link, tags: cat ? [cat] : [] });

    setAdded((prev) => ({ ...prev, [link.url]: true }));
    setTimeout(() => {
      setAdded((prev) => {
        const copy = { ...prev };
        delete copy[link.url];
        return copy;
      });
    }, 3000);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <input
          type="text"
          placeholder="Search suggested links…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search suggested links"
          className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 text-sm bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent focus:outline-none transition-all"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Filter by category"
          className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 text-sm bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent focus:outline-none transition-all"
        >
          <option value="">All Categories</option>
          {POPULAR_LINKS.map((cat) => (
            <option key={cat.category} value={cat.category}>
              {cat.category}
            </option>
          ))}
        </select>
      </div>

      {sections.length === 0 ? (
        <div className="py-8 text-center text-[var(--color-text-tertiary)] text-sm">
          No suggestions match “{search.trim()}”.
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((cat) => (
            <div key={cat.category} className="space-y-3">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] border-b border-[var(--color-border)] pb-2">
                {cat.category}
              </h3>
              <div className="space-y-2">
                {cat.links.map((link) => (
                  <button
                    key={link.url}
                    type="button"
                    disabled={isAdded(link.url)}
                    aria-label={
                      isAdded(link.url)
                        ? `${link.name} (already on your dashboard)`
                        : `Add ${link.name}`
                    }
                    onClick={() => handleAdd(link)}
                    className={`w-full flex items-center gap-3 py-3 px-3 rounded-lg transition group text-left ${
                      isAdded(link.url)
                        ? "bg-[color-mix(in_srgb,var(--color-success)_5%,transparent)] border border-[color-mix(in_srgb,var(--color-success)_20%,transparent)]"
                        : "hover:bg-[var(--color-bg-hover)] border border-transparent"
                    } focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-1`}
                  >
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[var(--color-bg-hover)] border border-[var(--color-border)]">
                      {failedIcons[link.url] ? (
                        <span
                          className="text-xs font-semibold uppercase text-[var(--color-text-tertiary)]"
                          aria-hidden="true"
                        >
                          {link.name.charAt(0)}
                        </span>
                      ) : (
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(
                            hostnameOf(link.url)
                          )}&sz=64`}
                          alt=""
                          aria-hidden="true"
                          width={64}
                          height={64}
                          loading="lazy"
                          decoding="async"
                          className="w-5 h-5 rounded"
                          onError={() => setFailedIcons((prev) => ({ ...prev, [link.url]: true }))}
                        />
                      )}
                    </span>

                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-[var(--color-text-primary)] truncate">
                        {link.name}
                      </span>
                      <span className="block text-xs text-[var(--color-text-tertiary)] truncate">
                        {hostnameOf(link.url)}
                      </span>
                    </span>

                    <span className="flex-shrink-0">
                      {isAdded(link.url) ? (
                        <span className="flex items-center gap-1 text-[var(--color-success)] text-xs">
                          <span aria-hidden="true">Added</span>
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="sr-only">Added</span>
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-tertiary)] group-hover:text-[var(--color-accent)] transition-colors">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestedLinksTab;
